// packages/ui/src/components/settings/ProviderSettings.tsx
// API key management with masked display and model selection

import { useState } from 'react'

export interface Provider {
  id: string
  name: string
  apiKey: string
  model: string
}

interface ProviderSettingsProps {
  providers: Provider[]
  onUpdate: (type: 'provider', payload: { id: string; apiKey?: string; model?: string }) => void
}

function maskKey(key: string): string {
  if (key.length <= 8) return '****'
  return key.slice(0, 4) + '****' + key.slice(-4)
}

export function ProviderSettings({ providers, onUpdate }: ProviderSettingsProps) {
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set())

  const toggleReveal = (id: string) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div data-testid="settings-providers" className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-200 mb-3">Providers</h2>
      <p className="text-sm text-gray-400 mb-4">
        Manage API keys and model selections for LLM providers.
      </p>
      <div className="space-y-3">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className="p-4 rounded-lg bg-gray-800/50 space-y-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-200">{provider.name}</span>
              <span
                data-testid={`provider-model-${provider.id}`}
                className="text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded"
              >
                {provider.model}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <code
                data-testid={`provider-key-${provider.id}`}
                className="flex-1 text-xs font-mono text-gray-400 bg-gray-900 px-3 py-1.5 rounded truncate"
              >
                {revealedKeys.has(provider.id) ? provider.apiKey : maskKey(provider.apiKey)}
              </code>
              <button
                data-testid={`provider-reveal-${provider.id}`}
                onClick={() => toggleReveal(provider.id)}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors px-2 py-1"
              >
                {revealedKeys.has(provider.id) ? 'Hide' : 'Reveal'}
              </button>
            </div>
          </div>
        ))}
        {providers.length === 0 && (
          <p className="text-sm text-gray-500">No providers configured.</p>
        )}
      </div>
    </div>
  )
}
